if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
      }

      connectedCallback() {
        window.requestAnimationFrame(() => this.initialize());
      }

      initialize() {
        if (this.initialized || !this.isConnected) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        if (!this.elements.viewer) {
          window.requestAnimationFrame(() => this.initialize());
          return;
        }

        this.initialized = true;
        this.applyColourFilter(this.dataset.selectedColour);
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.setupDesktopThumbnailControls();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupDesktopThumbnailControls() {
        if (this.dataset.desktopLayout !== 'thumbnail_slider') return;

        const thumbnailList = this.elements.thumbnails.slider;
        const previousButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');
        if (!thumbnailList || !previousButton || !nextButton) return;

        const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
        const updateButtons = () => {
          if (!isDesktop()) return;

          const maximumScroll = Math.max(0, thumbnailList.scrollHeight - thumbnailList.clientHeight);
          const canScroll = maximumScroll > 2;
          this.elements.thumbnails.classList.toggle('thumbnail-slider--fits', !canScroll);
          const previousDisabled = !canScroll || thumbnailList.scrollTop <= 2;
          const nextDisabled = !canScroll || thumbnailList.scrollTop >= maximumScroll - 2;
          previousButton.disabled = false;
          nextButton.disabled = false;
          previousButton.classList.toggle('is-disabled', previousDisabled);
          nextButton.classList.toggle('is-disabled', nextDisabled);
          previousButton.setAttribute('aria-disabled', previousDisabled);
          nextButton.setAttribute('aria-disabled', nextDisabled);
        };
        const scrollThumbnails = (direction, event) => {
          if (!isDesktop()) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          if (event.currentTarget.classList.contains('is-disabled')) return;
          const visibleThumbnail = Array.from(thumbnailList.querySelectorAll('.thumbnail-list__item:not([hidden])'))[0];
          const listStyles = window.getComputedStyle(thumbnailList);
          const gap = parseFloat(listStyles.rowGap || listStyles.gap) || 0;
          const distance = (visibleThumbnail?.offsetHeight || thumbnailList.clientHeight / 5) + gap;
          thumbnailList.scrollBy({ top: direction * distance, behavior: 'smooth' });
        };

        previousButton.addEventListener('click', scrollThumbnails.bind(this, -1), true);
        nextButton.addEventListener('click', scrollThumbnails.bind(this, 1), true);
        thumbnailList.addEventListener('scroll', updateButtons, { passive: true });
        window.addEventListener('resize', updateButtons, { passive: true });
        new ResizeObserver(updateButtons).observe(thumbnailList);
        const buttonStateObserver = new MutationObserver(updateButtons);
        buttonStateObserver.observe(previousButton, { attributes: true, attributeFilter: ['disabled'] });
        buttonStateObserver.observe(nextButton, { attributes: true, attributeFilter: ['disabled'] });
        window.requestAnimationFrame(updateButtons);
        window.setTimeout(updateButtons, 250);
      }

      applyColourFilter(selectedColour) {
        if (this.dataset.colourFilter !== 'true' || !selectedColour) return;

        this.dataset.selectedColour = selectedColour;
        this.querySelectorAll('[data-colour-group]').forEach((item) => {
          const group = (item.dataset.colourGroup || '').trim();
          item.hidden = Boolean(group && !group.split('|').includes(selectedColour));
        });

        const visibleMedia = Array.from(
          this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]:not([hidden])') || []
        );
        const currentMedia = this.elements.viewer?.querySelector(
          '.product__media-item[data-media-id].is-active:not([hidden])'
        );

        if (!currentMedia && visibleMedia[0]) this.setActiveMedia(visibleMedia[0].dataset.mediaId, false);

        this.elements.thumbnails?.querySelectorAll('.thumbnail-list__item:not([hidden])').forEach((item, index) => {
          item.dataset.mediaPosition = String(index + 1);
          const button = item.querySelector('button');
          const label = button?.getAttribute('aria-label');
          if (label) button.setAttribute('aria-label', label.replace(/\d+/, String(index + 1)));
        });

        const counterTotal = this.elements.viewer?.querySelector('.slider-counter--total');
        if (counterTotal) counterTotal.textContent = visibleMedia.length;
        this.updateVisibleCounter(currentMedia || visibleMedia[0]);

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      onSlideChanged(event) {
        this.updateVisibleCounter(event.detail.currentElement);
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      updateVisibleCounter(activeMedia) {
        if (!activeMedia || !this.elements.viewer) return;

        const visibleMedia = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]:not([hidden])')
        );
        const visiblePosition = visibleMedia.indexOf(activeMedia);
        if (visiblePosition < 0) return;

        const counterCurrent = this.elements.viewer.querySelector('.slider-counter--current');
        const counterTotal = this.elements.viewer.querySelector('.slider-counter--total');
        if (counterCurrent) counterCurrent.textContent = visiblePosition + 1;
        if (counterTotal) counterTotal.textContent = visibleMedia.length;
      }

      setActiveMedia(mediaId, prepend, preservePagePosition = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!preservePagePosition) {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);
        this.updateVisibleCounter(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (window.matchMedia('(min-width: 990px)').matches && this.dataset.desktopLayout === 'thumbnail_slider') {
          const thumbnailList = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = thumbnailList.scrollTop;
          const visibleBottom = visibleTop + thumbnailList.clientHeight;

          if (thumbnailTop < visibleTop) {
            thumbnailList.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          } else if (thumbnailBottom > visibleBottom) {
            thumbnailList.scrollTo({ top: thumbnailBottom - thumbnailList.clientHeight, behavior: 'smooth' });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

